import { defineCollection } from "@rebasepro/cms-types";
import { policy } from "@rebasepro/types";
import customersCollection from "./customers.js";

const walletPassesCollection = defineCollection({
    name: "Wallet Passes",
    singularName: "Wallet Pass",
    slug: "wallet_passes",
    table: "wallet_passes",
    description: "Apple Wallet (.pkpass) and Google Wallet pass records. Each pass contains a QR code scanned at POS for zero-friction identification.",
    securityRules: [
        { operation: "select", access: "public" },
        { operations: ["insert", "update", "delete"], roles: ["admin"] },
        // Ver la nota en transactions.ts: la emision de pases de wallet ocurre
        // en el servidor, sin usuario autenticado.
        { operations: ["insert", "update", "delete"], condition: policy.serverContext() }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid",
            admin: { readOnly: true }
        },
        customer: {
            name: "Customer",
            type: "relation",
            validation: { required: true },
            relation: {
                kind: "belongsTo",
                target: () => customersCollection,
                // 0.18 made a required belongsTo default to ON DELETE RESTRICT.
                // The live database, the migration baseline and
                // tests/foreign-key-parity.test.ts all have CASCADE here, and a
                // boot-time ensure would rewrite the constraint in production on
                // the first deploy of that default. Written down so nothing moves;
                // whether the ledger should refuse a customer delete is a
                // decision to take on purpose, with a migration, not by omission.
                onDelete: "cascade",
            }
        },
        platform: {
            name: "Platform",
            type: "string",
            validation: { required: true },
            enum: [
                { id: "apple", label: "Apple Wallet", color: "gray" },
                { id: "google", label: "Google Wallet", color: "blue" }
            ]
        },
        serial_number: {
            name: "Serial Number",
            type: "string",
            validation: { required: true, unique: true },
            description: "The card's loyalty id, also its QR (e.g. LOY-1234567)"
        },
        qr_payload: {
            name: "QR Payload",
            type: "string",
            validation: { required: true },
            description: "Encoded QR content; the same id as the serial (e.g. LOY-1234567)"
        },
        pass_url: {
            name: "Pass URL",
            type: "string",
            url: true,
            description: "URL to download/install the pass"
        },
        status: {
            name: "Status",
            type: "string",
            defaultValue: "active",
            enum: [
                { id: "active", label: "Active", color: "green" },
                { id: "revoked", label: "Revoked", color: "red" },
                { id: "expired", label: "Expired", color: "gray" }
            ]
        },
        last_updated_at: {
            name: "Last Push Update",
            type: "date",
            mode: "date_time",
            description: "Last time the card was written to Google Wallet. Empty until its Google link is first " +
                "opened; only cards with a date here get updates (src/google-card.ts).",
            admin: { readOnly: true }
        },
        created_at: {
            name: "Created At",
            type: "date",
            mode: "date_time",
            autoValue: "on_create",
            admin: { readOnly: true }
        }
    },
    admin: {
        icon: "Wallet",
        group: "Loyalty",
        defaultViewMode: "list",
        enabledViews: ["list", "table"],
        // The serial is the thing you match against a scan at the till, so it is
        // the first column rather than something to open the record for.
        listProperties: ["serial_number", "platform", "status"],
        previewProperties: ["customer"],
        openEntityMode: "side_panel",
        exportable: true,
        sort: ["created_at", "desc"],
        propertiesOrder: [
            "customer", "platform", "serial_number",
            "qr_payload", "pass_url", "status",
            "last_updated_at", "created_at"
        ]
    }
});

export default walletPassesCollection;
